import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Bell, Mail, MessageSquare, Phone, Send, RefreshCw, Search,
  CheckCircle, XCircle, Clock, AlertCircle, Eye, RotateCw,
  TrendingUp, Users, Calendar, Filter, ChevronRight, Loader2,
  BarChart3, PieChart, Activity, Zap, Settings, Download
} from "lucide-react";

const CHANNEL_INFO = {
  email: { icon: Mail, label: "Email", color: "bg-blue-100 text-blue-700" },
  sms: { icon: Phone, label: "SMS", color: "bg-green-100 text-green-700" },
  push: { icon: Bell, label: "Push", color: "bg-purple-100 text-purple-700" },
  in_app: { icon: MessageSquare, label: "In-App", color: "bg-amber-100 text-amber-700" }
};

const STATUS_INFO = {
  sent: { icon: CheckCircle, label: "Sent", color: "bg-green-100 text-green-700" },
  delivered: { icon: CheckCircle, label: "Delivered", color: "bg-emerald-100 text-emerald-700" },
  pending: { icon: Clock, label: "Pending", color: "bg-amber-100 text-amber-700" },
  failed: { icon: XCircle, label: "Failed", color: "bg-red-100 text-red-700" },
  mocked: { icon: Eye, label: "Mocked", color: "bg-slate-100 text-slate-700" }
};

export default function NotificationCenterPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard stats
  const [stats, setStats] = useState({
    total_sent: 0,
    email_sent: 0,
    sms_sent: 0,
    delivery_rate: 0,
    failed_count: 0,
    pending_count: 0
  });
  
  // Notification history
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Integration status
  const [integrations, setIntegrations] = useState({
    twilio_sms: { enabled: false, status: "disconnected" },
    resend_email: { enabled: false, status: "disconnected" },
    push: { enabled: false, status: "inactive" }
  });
  
  // Resend dialog
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resending, setResending] = useState(false);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get("/ehr/reminder-notifications/status"),
        api.get("/ehr/reminder-notifications/history?limit=100")
      ]);
      
      setIntegrations(statusRes.data.integrations || {});
      
      const historyData = historyRes.data.history || [];
      setHistory(historyData);
      setFilteredHistory(historyData);
      
      // Calculate stats from history
      const emailSent = historyData.filter(h => h.channels?.includes("email")).length;
      const smsSent = historyData.filter(h => h.channels?.includes("sms")).length;
      const failedCount = historyData.filter(h => 
        h.results?.some(r => r.status === "failed")
      ).length;
      const pendingCount = historyData.filter(h => 
        h.results?.some(r => r.status === "pending")
      ).length;
      
      const totalWithResults = historyData.filter(h => h.results?.length > 0).length;
      const successfulDeliveries = historyData.filter(h => 
        h.results?.every(r => r.success)
      ).length;
      
      setStats({
        total_sent: historyData.length,
        email_sent: emailSent,
        sms_sent: smsSent,
        delivery_rate: totalWithResults > 0 ? Math.round((successfulDeliveries / totalWithResults) * 100) : 100,
        failed_count: failedCount,
        pending_count: pendingCount
      });
      
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error(t("notificationCenter.loadError", "Failed to load notification data"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    // Filter history based on search and filters
    let filtered = [...history];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(n => 
        n.patient_id?.toLowerCase().includes(query) ||
        n.type?.toLowerCase().includes(query)
      );
    }
    
    if (channelFilter !== "all") {
      filtered = filtered.filter(n => n.channels?.includes(channelFilter));
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(n => 
        n.results?.some(r => r.status === statusFilter)
      );
    }
    
    setFilteredHistory(filtered);
  }, [history, searchQuery, channelFilter, statusFilter]);

  const handleResend = async () => {
    if (!selectedNotification) return;
    
    setResending(true);
    try {
      await api.post("/ehr/reminder-notifications/send", {
        patient_id: selectedNotification.patient_id,
        discipline: "RN", // Default
        visit_date: new Date().toISOString().split("T")[0],
        channels: selectedNotification.channels || ["email"]
      });
      toast.success(t("notificationCenter.resendSuccess", "Notification resent successfully"));
      setShowResendDialog(false);
      setSelectedNotification(null);
      loadDashboardData();
    } catch (error) {
      toast.error(t("notificationCenter.resendError", "Failed to resend notification"));
    } finally {
      setResending(false);
    }
  };

  const getChannelBadge = (channel) => {
    const info = CHANNEL_INFO[channel] || CHANNEL_INFO.email;
    const Icon = info.icon;
    return (
      <Badge className={info.color}>
        <Icon className="w-3 h-3 mr-1" />
        {info.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const info = STATUS_INFO[status] || STATUS_INFO.pending;
    const Icon = info.icon;
    return (
      <Badge className={info.color}>
        <Icon className="w-3 h-3 mr-1" />
        {info.label}
      </Badge>
    );
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="notification-center-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Bell className="w-8 h-8" />
                <h1 className="text-3xl font-bold">{t("notificationCenter.title", "Notification Center")}</h1>
              </div>
              <p className="text-indigo-200">
                {t("notificationCenter.subtitle", "Monitor and manage patient communication")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={loadDashboardData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                {t("common.refresh", "Refresh")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Integration Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className={`border-l-4 ${integrations.twilio_sms?.enabled ? "border-l-green-500" : "border-l-gray-300"}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Phone className={`w-5 h-5 ${integrations.twilio_sms?.enabled ? "text-green-600" : "text-gray-400"}`} />
                  <div>
                    <p className="font-medium">Twilio SMS</p>
                    <p className="text-xs text-muted-foreground">
                      {integrations.twilio_sms?.status === "connected" ? t("notificationCenter.connected", "Connected") : t("notificationCenter.disconnected", "Disconnected")}
                    </p>
                  </div>
                </div>
                <Badge className={integrations.twilio_sms?.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                  {integrations.twilio_sms?.enabled ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-l-4 ${integrations.resend_email?.enabled ? "border-l-blue-500" : "border-l-gray-300"}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className={`w-5 h-5 ${integrations.resend_email?.enabled ? "text-blue-600" : "text-gray-400"}`} />
                  <div>
                    <p className="font-medium">Resend Email</p>
                    <p className="text-xs text-muted-foreground">
                      {integrations.resend_email?.status === "connected" ? t("notificationCenter.connected", "Connected") : t("notificationCenter.disconnected", "Disconnected")}
                    </p>
                  </div>
                </div>
                <Badge className={integrations.resend_email?.enabled ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}>
                  {integrations.resend_email?.enabled ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-l-4 ${integrations.push?.enabled ? "border-l-purple-500" : "border-l-gray-300"}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className={`w-5 h-5 ${integrations.push?.enabled ? "text-purple-600" : "text-gray-400"}`} />
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-xs text-muted-foreground">
                      {integrations.push?.status === "active" ? t("notificationCenter.active", "Active") : t("notificationCenter.inactive", "Inactive")}
                    </p>
                  </div>
                </div>
                <Badge className={integrations.push?.enabled ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}>
                  {integrations.push?.enabled ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4" />
              {t("notificationCenter.tabs.dashboard", "Dashboard")}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
              <Clock className="w-4 h-4" />
              {t("notificationCenter.tabs.history", "History")}
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card data-testid="stat-total">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t("notificationCenter.stats.totalSent", "Total Sent")}</p>
                      <p className="text-3xl font-bold text-primary">{stats.total_sent}</p>
                    </div>
                    <Send className="w-10 h-10 text-primary/20" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="stat-delivery-rate">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t("notificationCenter.stats.deliveryRate", "Delivery Rate")}</p>
                      <p className="text-3xl font-bold text-green-600">{stats.delivery_rate}%</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-green-200" />
                  </div>
                  <Progress value={stats.delivery_rate} className="mt-3 h-2" />
                </CardContent>
              </Card>

              <Card data-testid="stat-email">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t("notificationCenter.stats.emailSent", "Emails Sent")}</p>
                      <p className="text-3xl font-bold text-blue-600">{stats.email_sent}</p>
                    </div>
                    <Mail className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="stat-sms">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t("notificationCenter.stats.smsSent", "SMS Sent")}</p>
                      <p className="text-3xl font-bold text-green-600">{stats.sms_sent}</p>
                    </div>
                    <Phone className="w-10 h-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Channel Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5" />
                    {t("notificationCenter.channelBreakdown", "Channel Breakdown")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-blue-500" />
                        <span>Email</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{stats.email_sent}</span>
                        <span className="text-muted-foreground text-sm">
                          ({stats.total_sent > 0 ? Math.round((stats.email_sent / stats.total_sent) * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                    <Progress value={stats.total_sent > 0 ? (stats.email_sent / stats.total_sent) * 100 : 0} className="h-2 [&>div]:bg-blue-500" />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-green-500" />
                        <span>SMS</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{stats.sms_sent}</span>
                        <span className="text-muted-foreground text-sm">
                          ({stats.total_sent > 0 ? Math.round((stats.sms_sent / stats.total_sent) * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                    <Progress value={stats.total_sent > 0 ? (stats.sms_sent / stats.total_sent) * 100 : 0} className="h-2 [&>div]:bg-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    {t("notificationCenter.deliveryStatus", "Delivery Status")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span>{t("notificationCenter.status.successful", "Successful")}</span>
                      </div>
                      <span className="font-bold text-green-600">
                        {stats.total_sent - stats.failed_count - stats.pending_count}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-amber-600" />
                        <span>{t("notificationCenter.status.pending", "Pending")}</span>
                      </div>
                      <span className="font-bold text-amber-600">{stats.pending_count}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <span>{t("notificationCenter.status.failed", "Failed")}</span>
                      </div>
                      <span className="font-bold text-red-600">{stats.failed_count}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* HISTORY TAB */}
          <TabsContent value="history" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={t("notificationCenter.searchPlaceholder", "Search by patient ID...")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                  
                  <Select value={channelFilter} onValueChange={setChannelFilter}>
                    <SelectTrigger className="w-40" data-testid="channel-filter">
                      <SelectValue placeholder={t("notificationCenter.filterChannel", "Channel")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("common.all", "All Channels")}</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="push">Push</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="status-filter">
                      <SelectValue placeholder={t("notificationCenter.filterStatus", "Status")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("common.all", "All Status")}</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="mocked">Mocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* History List */}
            <Card>
              <CardHeader>
                <CardTitle>{t("notificationCenter.historyTitle", "Notification History")}</CardTitle>
                <CardDescription>
                  {t("notificationCenter.historyDescription", "View and manage sent notifications")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {filteredHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                      <p className="text-muted-foreground">{t("notificationCenter.noHistory", "No notifications found")}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredHistory.map((notification, idx) => (
                        <div
                          key={notification.id || idx}
                          className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                          data-testid={`notification-${notification.id || idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline">{notification.type || "visit_reminder"}</Badge>
                                {notification.channels?.map((channel, i) => (
                                  <span key={i}>{getChannelBadge(channel)}</span>
                                ))}
                              </div>
                              
                              <p className="text-sm font-medium">
                                Patient: {notification.patient_id || "Unknown"}
                              </p>
                              
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(notification.created_at)}
                                </span>
                              </div>
                              
                              {/* Results */}
                              {notification.results?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {notification.results.map((result, i) => (
                                    <div key={i} className="flex items-center gap-1 text-xs">
                                      {getStatusBadge(result.status)}
                                      {result.error && (
                                        <span className="text-red-500 ml-1">{result.error}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedNotification(notification);
                                setShowResendDialog(true);
                              }}
                              data-testid={`resend-${notification.id || idx}`}
                            >
                              <RotateCw className="w-4 h-4 mr-1" />
                              {t("notificationCenter.resend", "Resend")}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Resend Dialog */}
      <Dialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("notificationCenter.resendTitle", "Resend Notification")}</DialogTitle>
            <DialogDescription>
              {t("notificationCenter.resendDescription", "Send this notification again to the patient")}
            </DialogDescription>
          </DialogHeader>
          
          {selectedNotification && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Patient ID</p>
                <p className="font-medium">{selectedNotification.patient_id}</p>
              </div>
              
              <div className="flex gap-2">
                <p className="text-sm text-muted-foreground">Channels:</p>
                {selectedNotification.channels?.map((channel, i) => (
                  <span key={i}>{getChannelBadge(channel)}</span>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResendDialog(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={handleResend} disabled={resending} data-testid="confirm-resend-btn">
              {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {t("notificationCenter.resend", "Resend")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
